// src/pages/pmc/PmcPaymentRegisterPane.js

import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import toast from "react-hot-toast";

const formatINR = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

function Badge({ status }) {
  const s = String(status || "").toLowerCase();

  const cls =
    s === "paid"
      ? "bg-emerald-100 text-emerald-700 border-emerald-300"
      : s === "approved"
      ? "bg-blue-100 text-blue-700 border-blue-300"
      : "bg-amber-100 text-amber-700 border-amber-300";

  return (
    <span className={`rounded-full border px-2 py-1 text-[10px] font-black ${cls}`}>
      {status || "Pending"}
    </span>
  );
}

function StatCard({ label, value, note }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-[11px] font-black uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-2xl font-black text-slate-950">
        {value}
      </div>
      {note && (
        <div className="mt-0.5 text-[11px] text-slate-400">
          {note}
        </div>
      )}
    </div>
  );
}

export default function PmcPaymentRegisterPane() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [savingId, setSavingId] = useState("");

  const [paymentMap, setPaymentMap] = useState({});
  const [utrMap, setUtrMap] = useState({});
  const [remarksMap, setRemarksMap] = useState({});

  const loadRows = async () => {
    try {
      setLoading(true);

      const snap = await getDocs(collection(db, "ra_bills"));

      const list = [];

      snap.forEach((d) => {
        const r = d.data();

        if (
          String(r.status || "").toLowerCase() !== "approved"
        ) {
          return;
        }

        list.push({
          id: d.id,
          ...r,
          projectName:
            r.projectName ||
            r.tenderTitle ||
            "Project",

          contractorName:
            r.contractorName ||
            r.vendorName ||
            "—",

          raNo:
            r.raNo ||
            d.id,

          totalAmount:
            Number(r.totalAmount || 0),

          paymentStatus:
            r.paymentStatus || "Pending",

          paidAmount:
            Number(r.paidAmount || 0),

          utrNo:
            r.utrNo || "",

          paymentRemarks:
            r.paymentRemarks || "",
        });
      });

      list.sort(
        (a, b) =>
          (b.createdAt?.seconds || 0) -
          (a.createdAt?.seconds || 0)
      );

      setRows(list);
    } catch (e) {
      console.error(e);
      toast.error("Payment register load failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const summary = useMemo(() => {
    const totalBills = rows.length;

    const totalApproved = rows.reduce(
      (s, r) => s + Number(r.totalAmount || 0),
      0
    );

    const paidAmount = rows.reduce(
      (s, r) => s + Number(r.paidAmount || 0),
      0
    );

    const balance = totalApproved - paidAmount;

    const paidCount = rows.filter(
      (r) =>
        String(r.paymentStatus || "").toLowerCase() === "paid"
    ).length;

    return {
      totalBills,
      totalApproved,
      paidAmount,
      balance,
      paidCount,
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return rows;

    return rows.filter((r) =>
      [
        r.projectName,
        r.contractorName,
        r.raNo,
        r.utrNo,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, search]);

  const markPaid = async (row) => {
    try {
      setSavingId(row.id);

      await updateDoc(doc(db, "ra_bills", row.id), {
        paymentStatus: "Paid",
        paidAmount:
          Number(paymentMap[row.id]) ||
          Number(row.totalAmount || 0),

        utrNo:
          utrMap[row.id] ||
          row.utrNo ||
          "",

        paymentRemarks:
          remarksMap[row.id] ||
          row.paymentRemarks ||
          "",

        paymentDate: serverTimestamp(),
      });

      toast.success("Payment updated.");
      await loadRows();
    } catch (e) {
      console.error(e);
      toast.error("Payment update failed.");
    } finally {
      setSavingId("");
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-3xl bg-slate-950 px-5 py-5 text-white">
        <div className="text-[11px] font-black uppercase tracking-[0.25em] text-emerald-300">
          PMC Payment Register
        </div>

        <div className="mt-2 text-xl font-black">
          Approved RA Payment Management
        </div>

        <div className="mt-1 text-sm text-slate-300">
          Mark approved RA bills as paid and update UTR details.
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-5">
        <StatCard
          label="Approved RA"
          value={summary.totalBills}
          note="Approved bills"
        />

        <StatCard
          label="Approved Amount"
          value={formatINR(summary.totalApproved)}
          note="Total approved"
        />

        <StatCard
          label="Paid Amount"
          value={formatINR(summary.paidAmount)}
          note="Released amount"
        />

        <StatCard
          label="Balance"
          value={formatINR(summary.balance)}
          note="Pending release"
        />

        <StatCard
          label="Paid Bills"
          value={summary.paidCount}
          note="Payment completed"
        />
      </div>

      <div className="rounded-2xl border border-emerald-100 bg-white p-4 shadow-sm">
        <label className="text-[11px] font-black uppercase text-slate-500">
          Search
        </label>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Project, contractor, RA No, UTR..."
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-xs font-bold"
        />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-300 bg-white">
        <table className="w-full min-w-[1500px] border-collapse text-[11px]">
          <thead>
            <tr className="bg-slate-950 text-white">
              <th className="border border-slate-400 px-3 py-3">
                Sr
              </th>

              <th className="border border-slate-400 px-3 py-3 text-left">
                Project
              </th>

              <th className="border border-slate-400 px-3 py-3 text-left">
                Contractor
              </th>

              <th className="border border-slate-400 px-3 py-3">
                RA No
              </th>

              <th className="border border-slate-400 px-3 py-3 text-right">
                RA Amount
              </th>

              <th className="border border-slate-400 px-3 py-3">
                Status
              </th>

              <th className="border border-slate-400 px-3 py-3 text-right">
                Paid Amount
              </th>

              <th className="border border-slate-400 px-3 py-3">
                UTR No
              </th>

              <th className="border border-slate-400 px-3 py-3">
                Remarks
              </th>

              <th className="border border-slate-400 px-3 py-3">
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={10}
                  className="border border-slate-300 px-3 py-8 text-center"
                >
                  Loading payment register...
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="border border-slate-300 px-3 py-8 text-center"
                >
                  No approved RA found.
                </td>
              </tr>
            ) : (
              filteredRows.map((r, idx) => (
                <tr key={r.id} className="hover:bg-emerald-50">
                  <td className="border border-slate-300 px-3 py-3 text-center font-black">
                    {idx + 1}
                  </td>

                  <td className="border border-slate-300 px-3 py-3">
                    {r.projectName}
                  </td>

                  <td className="border border-slate-300 px-3 py-3">
                    {r.contractorName}
                  </td>

                  <td className="border border-slate-300 px-3 py-3 font-black">
                    {r.raNo}
                  </td>

                  <td className="border border-slate-300 px-3 py-3 text-right font-black">
                    {formatINR(r.totalAmount)}
                  </td>

                  <td className="border border-slate-300 px-3 py-3 text-center">
                    <Badge status={r.paymentStatus} />
                  </td>

                  <td className="border border-slate-300 px-3 py-3">
                    <input
                      defaultValue={
                        r.paidAmount ||
                        r.totalAmount
                      }
                      onChange={(e) =>
                        setPaymentMap((p) => ({
                          ...p,
                          [r.id]: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 px-2 py-1 text-right"
                    />
                  </td>

                  <td className="border border-slate-300 px-3 py-3">
                    <input
                      defaultValue={r.utrNo}
                      onChange={(e) =>
                        setUtrMap((p) => ({
                          ...p,
                          [r.id]: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 px-2 py-1"
                    />
                  </td>

                  <td className="border border-slate-300 px-3 py-3">
                    <input
                      defaultValue={r.paymentRemarks}
                      onChange={(e) =>
                        setRemarksMap((p) => ({
                          ...p,
                          [r.id]: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 px-2 py-1"
                    />
                  </td>

                  <td className="border border-slate-300 px-3 py-3 text-center">
                    <button
                      disabled={savingId === r.id}
                      onClick={() => markPaid(r)}
                      className="rounded-full bg-emerald-600 px-4 py-2 text-[10px] font-black text-white hover:bg-emerald-700 disabled:bg-slate-300"
                    >
                      Mark Paid
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}